import React, { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { getTestComponents } from '../../../apiClients/backendApi';
import useAsync from '../../../hooks/useAsync';
import { useTitle } from '../../../systems/TitleManager';
import { Add } from '@mui/icons-material';
import { startCase } from 'lodash';
import DataGridDyl from '../../../components/DataGridDyl';
import { LevelText } from '@dyl/systems/LevelsManager';
import ScreenActions from '@dyl/components/ScreenActions';
import { Fab } from '@mui/material';
import LinkButton from '@dyl/components/LinkButton';
import quickDateFormat from '@dyl/utils/quickDateFormat';
import deleteTest from "@dyl/apiClients/backendApi/deleteTest";
import GenericDeleteAction from "@dyl/components/GenericDeleteAction";
import withLevel from "@dyl/utils/withLevel";
import formatNullValue from '@dyl/utils/formatNullValue';


export default withLevel(ComponentList);

function ComponentList({ globalLevel }) {
    useTitle('Components');
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [triggerReload, setTriggerReload] = useState();

    const components = useAsync(async () => {
        setLoading(true);

        try {
            return await getTestComponents(globalLevel.level_id);
        }
        catch {
            enqueueSnackbar('Failed to load components', { variant: 'error' });
        }
        finally {
            setLoading(false);
        }
    }, [triggerReload, globalLevel]) ?? [];

    const columns = useMemo(() => {
        /** @type {import('@mui/x-data-grid').GridColDef[]} */
        return [
            { headerName: 'Component ID', field: 'test_name', flex: 2 },
            { headerName: 'QPV Reference', field: 'qpv_reference', flex: 2, valueFormatter({ value }) {
                return formatNullValue(value);
            }
            },
            { headerName: 'Skill', field: 'skill_id', flex: 2, valueFormatter({ value }) {
                return startCase(value);
            }
            },
            { headerName: 'Level', field: 'level_id', flex: 1, renderCell({ value }) {
                return <LevelText levelId={value} />;
            }
            },
            { headerName: 'Last modified', field: 'updated_at', flex: 2, valueFormatter({ value }) {
                return quickDateFormat(value);
            }
            },
            { headerName: 'Delete', field: 'ADMIN_DELETE', flex: 2, renderCell({ row }) {
                return (<GenericDeleteAction
                        data={row}
                        promptTexts={
                            {
                                dialogTitle: () => 'Delete Test Component?',
                                dialogText: (data) => `Are you sure you want to delete test component "${data.test_name}"?`,
                                ongoingNotification: (data) => `Deleting test component "${data.test_name}"...`,
                                successNotification: (data, response) => `Deleted test component "${data.test_name}".`,
                                failureNotification: (data, response) =>
                                    `Failed to delete "${data.test_name}" as it belongs to following tests: ${response.test_groups.join(', ')}.`,
                                errorNotification: (data) => `Failed to delete test component "${data.test_name}".`
                            }
                        }
                        deleteAction={(data) => deleteTest(data.test_id)}
                        responseSuccessPredicate={(response) => response?.success}
                        onDelete={() => setTriggerReload(true)}
                    />)
                }
            }
        ];
    }, []);

    const getRowUrl = useCallback(({ row: component }) => {
        let { skill_id, test_id: componentId } = component;
        return `/${globalLevel.label}/components/${skill_id}/${componentId}`;
    }, [globalLevel]);

    return (
        <>
            <DataGridDyl
                rows={components}
                columns={columns}
                getRowId={getRowId}
                disableSelectionOnClick
                getRowUrl={getRowUrl}
                searchField='test_name'
                variant='links'
                loading={loading}
            />

            <ScreenActions>
                <LinkButton
                    component={Fab}
                    color='primary'
                    variant='extended'
                    size='large'
                    to={`/${globalLevel.label}/components/new`}
                >
                    <Add />&nbsp;New
                </LinkButton>
            </ScreenActions>
        </>
    );
}
const getRowId = component => component?.test_id;
